import React, {
  useEffect, useState, useRef, Dispatch, SetStateAction,
} from 'react';
import {
  View, StyleSheet, Text, Platform, FlatList,
} from 'react-native';
import Modal from 'react-native-modal';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../../../constants/Colors';
import { TemplateUpdateOption } from '../../../components/AssetManagement/AddTemplateModal/components/CreationOptionList/components/CreationOption';
import PressableStyled from '../../../components/PressableStyled';
import CloseModalIcon from '../../../components/CloseModalIcon';
import isMobile from '../../../components/IsMobile';
import Style from '../../../constants/Style';
import TemplateList from '../../../components/AssetManagement/AddTemplateModal/components/TemplateList';
import PresetTemplates from '../../../components/AssetManagement/AddTemplateModal/components/PresetTemplates';
import { Template } from '../../../constants/Types';
import Button from '../../../components/Button';
import CreationOptionList from '../../../components/AssetManagement/AddTemplateModal/components/CreationOptionList';

interface AddTemplateModalProps {
  isVisible: boolean,
  hide: () => void,
  templates: Template[],
  setAddTemplateOption: Dispatch<SetStateAction<TemplateUpdateOption>>,
  setSelectedTemplateId: Dispatch<SetStateAction<string>>,
}

const TemplatePresetModal = ({
  isVisible, hide, templates, setAddTemplateOption, setSelectedTemplateId,
}: AddTemplateModalProps) => {
  const insets: EdgeInsets = useSafeAreaInsets();
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [primedTemplateId, setPrimedTemplateId] = useState<string | null>(null);
  const templateList: React.Ref<FlatList> = useRef<FlatList>();
  const [initialOption, setInitialOption] = useState<TemplateUpdateOption | null>(null);

  useEffect(() => {
    if (isVisible) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    } else {
      setVisibilityToggleEnabled(true);
    }
  }, [isVisible]);

  useEffect(() => {
    if (initialOption === TemplateUpdateOption.CUSTOM) {
      setAddTemplateOption(TemplateUpdateOption.CUSTOM);
      setSelectedTemplateId(null);
    }
  }, [initialOption]);

  const closeModal: () => void = () => (visibilityToggleEnabled ? hide() : null);

  const onPressBack: () => void = () => {
    setInitialOption(null);
    setPrimedTemplateId(null);
  };

  const onPressContinue: () => void = () => {
    if (!primedTemplateId) return;
    setAddTemplateOption(initialOption);
    setSelectedTemplateId(primedTemplateId);
  };

  const modalTitle: () => string = () => {
    if (initialOption === TemplateUpdateOption.DEFAULT) return 'Choose a preset template';
    if (initialOption === TemplateUpdateOption.VARIANT) return 'Choose an existing template';
    return 'Choose preset template or create from scratch';
  };

  return <Modal
    {...{ isVisible }}
    onSwipeComplete={closeModal}
    onBackButtonPress={closeModal}
    onBackdropPress={closeModal}
    style={styles.modal}
    onModalHide={onPressBack}
  >
    <View style={[
      Style.modalContent, styles.container,
      { paddingBottom: styles.container.paddingBottom + insets.bottom },
    ]}>
      <Text style={[styles.title, Platform.OS === 'ios' && { marginTop: insets.top - 12 }]}>
        {modalTitle()}
      </Text>
      {[TemplateUpdateOption.DEFAULT, TemplateUpdateOption.VARIANT].includes(initialOption)
        && <TemplateList
          {...{ primedTemplateId, setPrimedTemplateId, templateList }}
          data={initialOption === TemplateUpdateOption.DEFAULT ? PresetTemplates : templates}
          testID={initialOption === TemplateUpdateOption.DEFAULT ? 'default-templates-list' : null}
        />}
      {initialOption ? <View style={styles.buttonContainer}>
        <Button
          onPress={onPressBack}
          title='Back'
        />
        <Button
          onPress={onPressContinue}
          affirmative
          title='Continue'
          testID='continue-btn'
          disabled={!primedTemplateId}
        />
      </View> : <CreationOptionList {...{ setInitialOption }} />}
      <PressableStyled
        onPress={hide}
        style={[
          Style.closeButton, Platform.OS === 'ios' && { top: Style.closeButton.top + insets.top },
        ]}
        testID='close-create-template-modal'
      >
        <CloseModalIcon />
      </PressableStyled>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  container: {
    paddingBottom: isMobile ? 16 : 40,
    margin: isMobile ? 16 : 20,
    borderRadius: isMobile ? 0 : 10,
    backgroundColor: '#FFF',
    width: isMobile ? '100%' : 530,
    minHeight: isMobile ? '100%' : 467,
    height: isMobile ? '100%' : undefined,
    paddingTop: isMobile ? '50%' : 60,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    marginBottom: 32,
    width: isMobile ? '75%' : 356,
    alignSelf: 'center',
  },
  buttonContainer: {
    alignSelf: 'center',
    flexDirection: 'row',
    marginTop: isMobile ? 27 : 40,
    paddingHorizontal: isMobile ? 16 : 40,
  },
});

export default TemplatePresetModal;
